import type { TimestampInMillis } from './dates';
import type { Email } from './email';
import type { UUID } from './identifier';

import type { UserAttributes, UserPreferences } from '@cobliteam/api-lib';

export interface UserData {
  hasCameraFlag: boolean;
  userPreferences?: UserPreferences;
  userAttributes: UserAttributes;
  userFleets: AccessibleFleet[];
}

export enum UserRole {
  MAP_VIEW = 'map_view',
  OPERATION = 'operation',
  MANAGER = 'fleet',
  ADMINISTRATOR = 'administrator',

  // Legacy Roles
  COBLER = 'cobler',
  ROUTING_USER = 'routing_user',
}

export interface User {
  id: UUID;
  name: string;
  fullName?: string;
  role: UserRole;
  lastLogin?: TimestampInMillis;
  email: Email;
  creationDate?: TimestampInMillis;
  groupsRestriction: string[];
  grantedPermissions: string[];
  hasCustomPermissions: boolean;
  active: boolean;
}

export interface UserFleet {
  fleetId: string;
  userId: UUID;
  role: UserRole;
  groupsRestriction: string[];
}

export interface SessionUser {
  id: UUID;
  name: string;
  role: UserRole;
  language?: string;
  lastLogin?: TimestampInMillis;
  email: Email;
  creationDate?: TimestampInMillis;
  timezone?: number;
  groupsRestriction: string[];
  grantedPermissions: UserPermission[];
}

export interface AccessibleFleet {
  id: UUID;
  name: string;
  associatedAt?: TimestampInMillis;
}

export enum UserPermission {
  ADMINISTRATE_API_INTEGRATIONS = 'administrate:api-integrations',
  BLOCK_VEHICLE = 'block:vehicle',
  COBLER_ADMIN = 'cobler:admin',
  CONFIG_FUEL_CARD = 'config:fuel-card',
  CREATE_UPDATE_DELETE_PLACES_OF_INTEREST = 'create-update-delete:places-of-interest',
  CREATE_UPDATE_DELETE_ROUTES = 'create-update-delete:routes',
  FULL_CONTROL_CHECKLIST = 'full-control:checklist',
  FULL_CONTROL_MAINTENANCE = 'full-control:maintenance',
  REQUEST_CAMERA_MEDIA = 'request:camera-media',
  CREATE_UPDATE_DELETE_VEHICLE = 'create-update-delete:vehicle',
  UPDATE_DRIVER_FACE_CLUSTER = 'update:driver-face-cluster',
  UPDATE_FLEET_INFO = 'update:fleet-info',
  VIEW_ANALYTICS_AND_REPORTS = 'view:analytics-and-reports',
  VIEW_CAMERA_MEDIA = 'view:camera-media',
  VIEW_FLEET_EXPENSES = 'view:fleet-expenses',
  VIEW_FLEET_SAFETY = 'view:fleet-safety',
  WRITE_FLEET_SAFETY = 'write:fleet-safety',
  VIEW_FUEL_CARD = 'view:fuel-card',
  VIEW_PLACES_OF_INTEREST = 'view:places-of-interest',
  VIEW_PROOF_OF_CONCLUSION = 'view:proof-of-conclusion',
  VIEW_ROUTES = 'view:routes',
  CREATE_UPDATE_DELETE_DRIVER = 'create-update-delete:driver',
  VIEW_USERS_AND_INVITES = 'view:users-and-invites',
  CREATE_UPDATE_DELETE_USERS_AND_INVITES = 'create-update-delete:users-and-invites',
  CONFIG_AND_VIEW_CUSTOM_ALERTS = 'config-and-view:custom-alerts',
  BASIC_PERMISSION = 'basic:permission',
  READ_HUBSPOT_CONTRACTS = 'read:hubspot-contracts',
  WRITE_HUBSPOT_FINANCE = 'write:hubspot-finance',
}
